import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import elfoMascotLogo from '../../assets/elfo-mascot-logo.png';
import { ReactComponent as FilterIcon } from '../../assets/icon-filter.svg';
import { ReactComponent as PlusIcon } from '../../assets/icon-plus.svg';
import { ReactComponent as RefreshIcon } from '../../assets/icon-refresh.svg';
import { postRequestAsync } from '../../helpers/apiHandler';
import { showAlert } from '../../store/actions/alertAction';
import './_templateList.scss';
import TemplateFilterDropdown from './TemplateFilterDropdown';

const StatusBadge = ({ status }) => {
  const badgeStyles = {
    APPROVED: { backgroundColor: 'rgba(46, 204, 113, 0.2)', color: 'rgba(39, 174, 96, 1)' },
    PENDING: { backgroundColor: 'rgba(255, 179, 71, 0.2)', color: 'rgba(255, 179, 71, 1)' },
    REJECTED: { backgroundColor: 'rgba(231, 76, 60, 0.2)', color: 'rgba(231, 76, 60, 1)' },
  };

  return (
    <span className={`badge`} style={{ ...badgeStyles[status], padding: '0.5em 0' }}>
      {status}
    </span>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
};

const ActionButton = ({ wabaId, phoneNumberId, templateId }) => (
  <div className="d-flex justify-content-center align-items-center gap-1">
    <Button
      as={Link}
      to={`/dashboard/whatsapp/phone-number-details/${wabaId}/${phoneNumberId}/templates/${templateId}`}
    >
      Show Detail
    </Button>
  </div>
);

ActionButton.propTypes = {
  wabaId: PropTypes.string.isRequired,
  phoneNumberId: PropTypes.string.isRequired,
  templateId: PropTypes.string.isRequired,
};

const TemplateList = ({ phoneNumberId, wabaId }) => {
  const dispatch = useDispatch();
  const { access_token } = useSelector((state) => state.auth.user);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [paging, setPaging] = useState({
    before: '',
    after: '',
    next: '',
    previous: '',
  });

  const statusMapping = {
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
  };

  const fetchTemplates = useCallback(
    async (cursor = '') => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await postRequestAsync(
          '/v2/whatsapp/get-phone-templates',
          {
            phone_number_id: phoneNumberId,
            waba_id: wabaId,
            cursor,
          },
          access_token.token
        );

        if (res.success) {
          const { data } = res.data;
          setTemplates((prevTemplates) =>
            cursor
              ? // Only keep last 100 templates to prevent memory issues
                [...prevTemplates.slice(-100), ...(data.data || [])]
              : data.data || []
          );
          setPaging(data.paging);
        } else {
          throw new Error(res.message);
        }
      } catch (err) {
        setError(err.message);
        dispatch(
          showAlert('Error while fetching phone templates. Please try again later.', 'danger')
        );
      } finally {
        setIsLoading(false);
      }
    },
    [phoneNumberId, wabaId, access_token.token, dispatch]
  );

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (isMounted) {
        await fetchTemplates();
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [fetchTemplates]);

  const handleLoadMore = useCallback(() => {
    if (paging.after) {
      fetchTemplates(paging.after);
    }
  }, [paging.after, fetchTemplates]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      // Filter by status if any statuses are selected
      if (selectedStatuses.length > 0) {
        // Get all actual status values that should be included based on selected filter options
        const allowedStatuses = selectedStatuses.flatMap(
          (status) => statusMapping[status.value] || []
        );
        if (!allowedStatuses.includes(template.status)) {
          return false;
        }
      }

      // Filter by search query
      if (searchQuery && !template.name.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }

      return true;
    });
  }, [templates, selectedStatuses, searchQuery]);

  const tableColumns = useMemo(
    () => [
      {
        id: 'fixed-left',
        name: 'Template ID',
        selector: (row) => row.id,
        sortable: true,
      },
      {
        name: 'Template Name',
        selector: (row) => row.name,
        sortable: true,
      },
      {
        name: 'Language',
        selector: (row) => row.language.toUpperCase(),
        sortable: true,
        width: '120px',
      },
      {
        name: 'Status',
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => <StatusBadge status={row.status} />,
      },
      {
        name: 'Category',
        selector: (row) =>
          row.sub_category ? `${row.category} • ${row.sub_category}` : row.category,
        sortable: true,
      },
      {
        name: 'Actions',
        cell: (row) => (
          <ActionButton wabaId={wabaId} phoneNumberId={phoneNumberId} templateId={row.id} />
        ),
      },
    ],
    [wabaId, phoneNumberId]
  );

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="card border-0 wa-template-card">
      <div className="card-body p-0">
        <div className="d-flex flex-column p-4 gap-2">
          <h5 className="card-title" style={{ marginBottom: '0', fontSize: '20px' }}>
            Templates
          </h5>
          <div className="d-flex justify-content-end gap-1">
            <div className="search-container d-flex mr-auto">
              <div className="search-form position-relative" style={{ flex: 1 }}>
                <FilterIcon className="search-icon" />
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            <div className="d-flex gap-1">
              <TemplateFilterDropdown
                value={selectedStatuses}
                onChange={setSelectedStatuses}
                options={[
                  { value: 'PENDING', label: 'Pending' },
                  { value: 'APPROVED', label: 'Approved' },
                  { value: 'REJECTED', label: 'Rejected' },
                ]}
              />
              <Button
                variant="outline-primary"
                onClick={() => {
                  setSelectedStatuses([]);
                  setSearchQuery('');
                  fetchTemplates();
                }}
                className="btn-with-icon"
              >
                <RefreshIcon className="btn-icon" />
                Refresh
              </Button>
              <Button
                as={Link}
                variant="primary"
                to={`/dashboard/whatsapp/phone-number-details/${wabaId}/${phoneNumberId}/templates/create`}
                className="btn-with-icon"
              >
                <PlusIcon className="btn-icon" />
                Add New Template
              </Button>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" />
          </div>
        ) : filteredTemplates.length > 0 ? (
          <>
            <DataTable
              data={filteredTemplates}
              columns={tableColumns}
              progressPending={false}
              pagination
              paginationServer
              paginationTotalRows={templates.length}
              dense
            />
            {paging.after && (
              <Button
                onClick={handleLoadMore}
                disabled={isLoading}
                className="mt-3 mb-4 mx-auto d-block"
                variant="outline-primary"
              >
                {isLoading ? <Spinner size="sm" /> : 'Load More'}
              </Button>
            )}
          </>
        ) : (
          <div className="empty-state-container">
            <div className="empty-state-content">
              <img src={elfoMascotLogo} alt="Elfo Mascot" className="empty-state-logo" />
              <h3 className="empty-state-title">No Templates Found</h3>
              <p className="empty-state-description">
                {templates.length > 0
                  ? 'No templates match your current filters. Try adjusting your search criteria.'
                  : "You don't have any templates yet. Create your first template to get started."}
              </p>
              <Button
                as={Link}
                variant="primary"
                to={`/dashboard/whatsapp/phone-number-details/${wabaId}/${phoneNumberId}/templates/create`}
                className="empty-state-button btn-with-icon"
              >
                <PlusIcon className="btn-icon" />
                Create New Template
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

TemplateList.propTypes = {
  phoneNumberId: PropTypes.string.isRequired,
  wabaId: PropTypes.string.isRequired,
};

export default React.memo(TemplateList);
